import React, { useState, useEffect, lazy, Suspense } from 'react'
import { uuid } from 'uuidv4'
import { useLocation, useNavigate } from 'react-router-dom'
import Col from 'antd/lib/col'
import Row from 'antd/lib/row'
import Spin from 'antd/lib/spin'
import Tabs from 'antd/lib/tabs'
import Button from 'antd/lib/button'
import Tooltip from 'antd/lib/tooltip'
import {
  UnorderedListOutlined,
  MoreOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons'
import Products from './Products'
import Modifiers from './Modifiers'
import ModifierForm from './Modifiers/Form'
import Categories from './Products/Categories'
import ModifierGroups from './Modifiers/ModifierGroups'
import ModifierGroupsArchiveList from './Modifiers/ModifierGroups/ModifierGroupsArchiveList'
import ArchivedCategoriesList from './Products/Categories/ArchivedCategoriesList'
import { StyledDrawer, StyledSlerpDrawer } from '../Common/Drawer'
import { useMerchant } from 'hooks/useGlobalContext/useMerchant'

const ProductForm = lazy(() => import('./Products/Form'))

const { TabPane } = Tabs

const ProductsAndModifiers = () => {
  const location = useLocation() || {}
  const navigate = useNavigate()
  const { merchantData } = useMerchant()
  const [productDrawerVisible, setProductDrawerVisible] =
    useState<boolean>(false)
  const [categoryDrawerVisible, setCategoryDrawerVisible] =
    useState<boolean>(false)
  const [modifierGroupDrawerVisible, setModifierGroupDrawerVisible] =
    useState<boolean>(false)
  const [modiferDrawerVisible, setModifierDrawerVisible] =
    useState<boolean>(false)
  const [activeKey, setActiveKey] = useState<string>('products')
  const onProductFormSuccessCallback = () => {
    setProductDrawerVisible(false)
    navigate('/products')
  }
  const onModifierFormSuccessCallback = () => {
    setModifierDrawerVisible(false)
    navigate('/modifiers')
  }

  useEffect(() => {
    if (location) {
      const defaultActiveKey = location.pathname.match('products')
        ? 'products'
        : 'modifiers'
      setActiveKey(defaultActiveKey)

      const path = location.pathname
      if (path.includes('/products/categories')) {
        setCategoryDrawerVisible(true)
      } else if (path === '/products/new') {
        setProductDrawerVisible(true)
      } else if (path.includes('/modifiers/modifier-groups')) {
        setModifierGroupDrawerVisible(true)
      } else if (path === '/modifiers/new') {
        setModifierDrawerVisible(true)
      } else {
        setCategoryDrawerVisible(false)
        setProductDrawerVisible(false)
        setModifierGroupDrawerVisible(false)
        setModifierDrawerVisible(false)
      }
    } else {
      setActiveKey('products')
    }
  }, [location])

  return (
    <>
      <StyledSlerpDrawer
        width={640}
        onClose={() => {
          setProductDrawerVisible(false)
          navigate('/products')
        }}
        open={productDrawerVisible}
        data-testid='add-product-drawer'
        title={
          <>
            Add Product
            <Tooltip
              title={
                <a
                  href='https://support.slerp.com/knowledge/creating-products'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Creating Products
                </a>
              }
            >
              <QuestionCircleOutlined className='_ml-12' />
            </Tooltip>
          </>
        }
      >
        {({ formRef }) => (
          <Tabs>
            <TabPane tab='General' key='1'>
              <Suspense fallback={<Spin />}>
                <ProductForm
                  key={uuid()}
                  isSeoEnabled={!!merchantData?.is_seo_enabled}
                  discardCallback={onProductFormSuccessCallback}
                  successCallback={onProductFormSuccessCallback}
                  formRef={formRef}
                />
              </Suspense>
            </TabPane>
          </Tabs>
        )}
      </StyledSlerpDrawer>

      <StyledDrawer
        width={720}
        onClose={() => {
          setCategoryDrawerVisible(false)
          navigate('/products')
        }}
        open={categoryDrawerVisible}
        data-testid='categories-drawer'
        title='Categories'
      >
        <Tabs>
          <TabPane tab='Active' key='1'>
            <Categories key={uuid()} />
          </TabPane>
          <TabPane tab='Archived' key='2'>
            <ArchivedCategoriesList key={uuid()} />
          </TabPane>
        </Tabs>
      </StyledDrawer>

      <StyledDrawer
        width={720}
        onClose={() => {
          setModifierDrawerVisible(false)
          navigate('/modifiers')
        }}
        open={modiferDrawerVisible}
        data-testid='add-modifier-drawer'
        title='Add Modifier'
      >
        <Tabs>
          <TabPane tab='General' key='1'>
            <ModifierForm
              key={uuid()}
              discardCallback={onModifierFormSuccessCallback}
              successCallback={onModifierFormSuccessCallback}
            />
          </TabPane>
        </Tabs>
      </StyledDrawer>

      <StyledDrawer
        width={720}
        onClose={() => {
          setModifierGroupDrawerVisible(false)
          navigate('/modifiers')
        }}
        open={modifierGroupDrawerVisible}
        data-testid='modifier-groups-drawer'
        title='Modifier Groups'
      >
        <Tabs>
          <TabPane tab='Active' key='1'>
            <ModifierGroups />
          </TabPane>
          <TabPane tab='Archived' key='2'>
            <ModifierGroupsArchiveList />
          </TabPane>
        </Tabs>
      </StyledDrawer>
      <Row>
        <Col span={24}>
          {activeKey === 'products' && (
            <Row justify='end' gutter={[8, 8]}>
              <Col className='orders-search'>
                <Button
                  icon={<UnorderedListOutlined />}
                  onClick={() => navigate('/products/categories')}
                  data-testid='show-categories-drawer-btn'
                >
                  CATEGORIES
                </Button>
              </Col>
              <Col>
                <Tooltip
                  placement='bottomLeft'
                  title={
                    <span>
                      <a
                        href='https://support.slerp.com/knowledge/creating-products'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        Here's our best practice guide
                      </a>{' '}
                      to creating products. Be sure once you've added a product
                      that you make it avaliable in the stores' inventories for
                      it to be live at the relavent locations.
                    </span>
                  }
                >
                  <Button
                    onClick={() => navigate('/products/new')}
                    data-testid='add-product-btn'
                  >
                    ADD
                    <MoreOutlined />
                  </Button>
                </Tooltip>
              </Col>
            </Row>
          )}
          {activeKey === 'modifiers' && (
            <Row justify='end' gutter={[8, 8]}>
              <Col>
                <Tooltip
                  placement='left'
                  title={
                    <span>
                      See{' '}
                      <a
                        href='https://support.slerp.com/knowledge/modifiers'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        these
                      </a>{' '}
                      as categories for your add-ons. For example, 'Add a side?'
                      is a group of modifiers featuring your side dishes.
                    </span>
                  }
                >
                  <Button
                    icon={<UnorderedListOutlined />}
                    onClick={() => navigate('/modifiers/modifier-groups')}
                    data-testid='show-modifier-groups-drawer-btn'
                  >
                    MODIFIER GROUPS
                  </Button>
                </Tooltip>
              </Col>
              <Col>
                <Button
                  onClick={() => navigate('/modifiers/new')}
                  data-testid='add-modifier-btn'
                >
                  ADD
                  <MoreOutlined />
                </Button>
              </Col>
            </Row>
          )}
          <Row>
            <Col span={24}>
              <Tabs
                type='card'
                activeKey={activeKey}
                onChange={(value: string) => {
                  setActiveKey(value)
                  navigate(`/${value}`)
                }}
                data-testid='products-modifiers-tabs'
              >
                <TabPane
                  tab={<a data-testid='products-tab-pane'>PRODUCTS</a>}
                  key='products'
                >
                  <Products />
                </TabPane>
                <TabPane
                  tab={<a data-testid='modifiers-tab-pane'>MODIFIERS</a>}
                  key='modifiers'
                >
                  <Modifiers />
                </TabPane>
              </Tabs>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  )
}

export default ProductsAndModifiers
